import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';

const useStyles = makeStyles({
  '@keyframes tableScrollHintBounceVertical': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(4px)' },
  },
  '@keyframes tableScrollHintBounceHorizontal': {
    '0%, 100%': { transform: 'translateX(0)' },
    '50%': { transform: 'translateX(4px)' },
  },
  containerVertical: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    // The wrapper is click-through; only the button itself is interactive.
    pointerEvents: 'none',
    zIndex: 4,
  },
  containerHorizontal: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 16,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 4,
  },
  button: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    padding: 0,
    borderRadius: '50%',
    border: 'none',
    // Translucent so it reads as a floating overlay rather than solid chrome.
    backgroundColor: 'rgba(9, 71, 185, 0.55)',
    color: '#FFFFFF',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)',
    cursor: 'pointer',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    '&:hover': { backgroundColor: 'rgba(67, 128, 237, 0.75)' },
  },
  buttonVertical: {
    animation: '$tableScrollHintBounceVertical 1.5s ease-in-out infinite',
  },
  buttonHorizontal: {
    animation: '$tableScrollHintBounceHorizontal 1.5s ease-in-out infinite',
  },
  icon: {
    fontSize: 28,
  },
});

interface Props {
  /** Ref to the scrollable container (e.g. a MUI TableContainer). */
  scrollRef: React.RefObject<HTMLElement>;
  /** Pixels from the scrolled-past edge within which the table is treated as fully scrolled. */
  threshold?: number;
  /** Which overflow axis this indicator watches and nudges. Defaults to vertical. */
  direction?: 'vertical' | 'horizontal';
}

/**
 * Floating arrow shown at the scrolled-past edge of a scrollable table while there is more
 * content beyond the fold in that direction, and hidden once the container is scrolled to that
 * edge. The container it points at must have a positioned ancestor for the absolute placement to
 * anchor correctly. Clicking it nudges the container further in that direction.
 */
export default function TableScrollIndicator({
  scrollRef,
  threshold = 8,
  direction = 'vertical',
}: Props) {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const remaining =
        direction === 'horizontal'
          ? el.scrollWidth - el.clientWidth - el.scrollLeft
          : el.scrollHeight - el.clientHeight - el.scrollTop;
      setVisible(remaining > threshold);
    };
    update();

    // The ResizeObserver and MutationObserver below can each fire synchronously and repeatedly
    // while the table's row set is being replaced (e.g. switching the task scope filter), and
    // `update` calls setState directly from their native callbacks. Reacting to every single
    // notification synchronously causes the browser to report "ResizeObserver loop completed
    // with undelivered notifications" as the resulting re-renders reflow the very element being
    // observed. Coalescing bursts of notifications into a single rAF-deferred update avoids that
    // synchronous observe -> setState -> reflow cycle.
    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    el.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    // Recompute when the viewport size changes and when rows/columns are added or removed, since
    // a capped-size container's own box may not resize as its content grows.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleUpdate) : undefined;
    resizeObserver?.observe(el);
    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(scheduleUpdate) : undefined;
    mutationObserver?.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [scrollRef, threshold, direction]);

  if (!visible) return null;

  const nudge = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (direction === 'horizontal') {
      el.scrollBy({ left: el.clientWidth * 0.8, behavior: 'smooth' });
    } else {
      el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
    }
  };

  const isHorizontal = direction === 'horizontal';

  return (
    <div className={isHorizontal ? classes.containerHorizontal : classes.containerVertical}>
      <button
        type="button"
        className={`${classes.button} ${
          isHorizontal ? classes.buttonHorizontal : classes.buttonVertical
        }`}
        onClick={nudge}
        aria-label={isHorizontal ? 'Scroll table right' : 'Scroll table down'}
      >
        {isHorizontal ? (
          <KeyboardArrowRight className={classes.icon} />
        ) : (
          <KeyboardArrowDown className={classes.icon} />
        )}
      </button>
    </div>
  );
}
